// API base do backend catalogo-admin.
// Em prod o nginx faz reverse proxy de /catalogo-admin/api → :3013/api,
// então usamos o prefixo /catalogo-admin. Em dev o vite proxy redireciona /api,
// por isso o base muda dependendo do ambiente.
export const API_BASE = import.meta.env.DEV ? '' : '/catalogo-admin'

export type Me = {
  id: string
  email: string | null
  phone: string | null
  name: string
  is_admin: boolean
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  })
  if (r.status === 401) {
    throw new Error('nao_autenticado')
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => '')
    throw new Error(`HTTP ${r.status}: ${txt}`)
  }
  return r.json() as Promise<T>
}

export async function getMe(): Promise<Me> {
  const { user } = await http<{ user: Me }>('/api/me')
  return user
}

export type ProdutoResumo = {
  codigo: string
  nome: string
  preco: string
  estoque: number
  grupo: string | null
  categoria_nome: string | null
  departamento: string | null
  marca: string | null
  tem_meta: boolean
  qtd_imagens: number
  qtd_atributos: number
  atualizado_em: string
}

export type ListarProdutosParams = {
  q?: string
  incompletos?: boolean
  limit?: number
  offset?: number
  departamento?: string
  enriquecimento?: string
  estoque_min?: number
  estoque_max?: number
  ordenar?: string
  direcao?: 'asc' | 'desc'
}

export async function listarProdutos(params: ListarProdutosParams = {}): Promise<{ data: ProdutoResumo[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.incompletos) qs.set('incompletos', 'true')
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.offset) qs.set('offset', String(params.offset))
  if (params.departamento) qs.set('departamento', params.departamento)
  if (params.enriquecimento) qs.set('enriquecimento', params.enriquecimento)
  if (params.estoque_min != null) qs.set('estoque_min', String(params.estoque_min))
  if (params.estoque_max != null) qs.set('estoque_max', String(params.estoque_max))
  if (params.ordenar) qs.set('ordenar', params.ordenar)
  if (params.direcao) qs.set('direcao', params.direcao)
  return http(`/api/produtos${qs.toString() ? `?${qs}` : ''}`)
}

export type Departamento = { departamento: string; total: number }

export async function listarDepartamentos(): Promise<Departamento[]> {
  return http('/api/produtos/departamentos')
}

export type ProdutoDetalhe = {
  produto: {
    codigo: string
    nome: string
    descricao: string | null
    descricao_curta: string | null
    preco_venda: string
    estoque_atual: number
    grupo: string | null
    categoria_nome: string | null
    departamento: string | null
    marca: string | null
    marca_override: string | null
    peso: string | null
    peso_override: string | null
    altura: number | null
    largura: number | null
    comprimento: number | null
    descricao_longa: string | null
    bullets: string[] | null
    atualizado_em: string
  }
  imagens: Array<{
    id: number
    ordem: number
    url: string
    alt: string | null
    tipo: string | null
    canais: string[]
    largura: number | null
    altura: number | null
  }>
  atributos: Array<{ id: number; chave: string; valor: unknown; ordem: number }>
}

export async function buscarProduto(codigo: string): Promise<ProdutoDetalhe> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}`)
}

export type MetaInput = {
  descricao_longa: string | null
  bullets: string[] | null
  marca_override: string | null
  peso: number | null
  altura: number | null
  largura: number | null
  comprimento: number | null
}

export async function salvarMeta(codigo: string, meta: MetaInput): Promise<void> {
  await http(`/api/produtos/${encodeURIComponent(codigo)}/meta`, {
    method: 'PUT',
    body: JSON.stringify(meta),
  })
}

export async function adicionarAtributo(
  codigo: string,
  chave: string,
  valor: unknown,
): Promise<{ id: number }> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}/atributos`, {
    method: 'POST',
    body: JSON.stringify({ chave, valor }),
  })
}

export async function removerAtributo(id: number): Promise<void> {
  await http(`/api/produtos/atributos/${id}`, { method: 'DELETE' })
}

// ─── Imagens ─────────────────────────────────────────────────────────────────
export type ImagemUploadResultado = {
  id: number
  ordem: number
  url: string
  alt: string | null
  tipo: string | null
  canais: string[]
  largura: number | null
  altura: number | null
  bytes: number
}

export async function uploadImagem(
  codigo: string,
  arquivo: File,
  meta: { canais?: string[]; tipo?: string | null; alt?: string | null } = {},
): Promise<ImagemUploadResultado> {
  const fd = new FormData()
  fd.append('arquivo', arquivo)
  if (meta.canais) fd.append('canais', JSON.stringify(meta.canais))
  if (meta.tipo) fd.append('tipo', meta.tipo)
  if (meta.alt) fd.append('alt', meta.alt)

  const r = await fetch(`${API_BASE}/api/produtos/${encodeURIComponent(codigo)}/imagens`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
  })
  if (!r.ok) {
    const txt = await r.text().catch(() => '')
    throw new Error(`HTTP ${r.status}: ${txt}`)
  }
  return r.json()
}

export async function atualizarImagem(
  id: number,
  patch: { canais?: string[]; tipo?: string | null; alt?: string | null; ordem?: number },
): Promise<void> {
  await http(`/api/produtos/imagens/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export async function removerImagem(id: number): Promise<void> {
  await http(`/api/produtos/imagens/${id}`, { method: 'DELETE' })
}

export async function reordenarImagens(codigo: string, ids: number[]): Promise<void> {
  await http(`/api/produtos/${encodeURIComponent(codigo)}/imagens/reordenar`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

// ─── IA ──────────────────────────────────────────────────────────────────────
export type IaDescricaoResultado = {
  descricao: string
  bullets: string[]
  alt_text: string
  usage: {
    input_tokens: number
    output_tokens: number
    cache_creation_input_tokens: number
    cache_read_input_tokens: number
  }
}

export async function gerarDescricaoIA(codigo: string): Promise<IaDescricaoResultado> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}/ia/descricao`, {
    method: 'POST',
  })
}
